import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { PageHeader } from '../shell';
import { Tabs, useToast } from '../organisms';
import { useCurrentUser } from '../lib/useCurrentUser';
import { useUserNames } from '../lib/useUserNames';
import { ArrivedCell, money, roleLabel, type CommissionAllocation, type CommissionStatus } from '../organisms/CommissionArrived';

/**
 * CommissionsReport — /v2/commissions
 *
 * The report Joe reconciles against PTS (ATA part 2 §4). One dataset, three views:
 *   Report   per-person totals by status, then every invoice with its allocation lines
 *   Audit    properties whose split does not total 100 or misses a bucket; properties with
 *            settled savings and no invoice; one person's property list with their splits
 *   Close    admin only: mark paid invoices Commission Paid at year end
 *
 * Who sees what is decided on the server: a firm-wide role sees everything and filters down;
 * anyone else sees only the invoices they hold a line on, and only their own line beside the
 * invoice total. The money-blind administrator gets the structure with the dollars redacted.
 *
 * Arrived is never blank: while an invoice is unpaid the amount still expected shows in the
 * brand's status red; once fully paid the figure turns status green.
 */

type Status = CommissionStatus;
type Allocation = CommissionAllocation;

interface InvoiceGroup {
  invoice_id?: string; invoice_number?: string | null; year: number | null; status?: string; commission_status: Status;
  invoice_date?: string | null; status_date?: string | null; last_paid_date?: string | null; invoice_amount?: number | null;
  property_id: string | null; property_name: string | null; client_id: string | null; client_name: string | null;
  allocations: Allocation[];
}
interface EmployeeRow {
  employee: string; name: string; lines: number;
  by_status: Record<string, { invoiced?: number; arrived?: number; lines: number }>;
  total_invoiced?: number; total_arrived?: number;
}
interface Report { scope: 'all' | 'own'; statuses: Status[]; invoices: InvoiceGroup[]; non_billable: InvoiceGroup[]; per_employee: EmployeeRow[] }
interface SplitAudit { deal_id: string; property_id: string | null; property_name: string | null; client_name: string | null; has_template: boolean; total: number; missing_labels: string[] }
interface UnbilledAudit { property_id: string; property_name: string; client_name: string | null; tax_year: number; reduction: number | string; appeals: number | string }
interface ByAgentAudit { deal_id: string; property_name: string | null; client_name: string | null; total: number; my_percent: number; lines: Array<{ role_label: string; assignee_name: string; percent: number; mine: boolean }> }

const FIRM_WIDE = new Set(['super_admin', 'admin', 'administrator']);

const STATUS_VARIANT: Record<Status, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  Paid: 'success', Mailed: 'warning', Void: 'neutral', NonBillable: 'info', 'Commission Paid': 'success',
};

export function CommissionsReport() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const firmWide = FIRM_WIDE.has(me?.role ?? '');
  const users = useUserNames();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('report');
  const [year, setYear] = useState('');
  const [employee, setEmployee] = useState('');
  const [clientId, setClientId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const params = useMemo(() => {
    const q = new URLSearchParams();
    if (year) q.set('year', year);
    if (employee) q.set('employee', employee);
    if (clientId) q.set('client_id', clientId);
    if (propertyId) q.set('property_id', propertyId);
    if (status) q.set('status', status);
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    return q.toString();
  }, [year, employee, clientId, propertyId, status, from, to]);

  const report = useQuery<Report>({
    queryKey: ['commissions-report', params],
    queryFn: ({ signal }) => api.get<Report>(`/api/commissions/report${params ? `?${params}` : ''}`, signal),
  });
  const clients = useQuery<Array<{ id: string; name: string | null }>>({ queryKey: ['clients-list'], queryFn: ({ signal }) => api.get('/api/clients', signal) });
  const properties = useQuery<Array<{ id: string; name: string | null; client_id?: string | null }>>({ queryKey: ['properties-list'], queryFn: ({ signal }) => api.get('/api/properties', signal) });
  const auditSplits = useQuery<{ rows: SplitAudit[] }>({ queryKey: ['commissions-audit-splits'], queryFn: ({ signal }) => api.get('/api/commissions/audit/splits', signal), enabled: tab === 'audit' });
  const auditUnbilled = useQuery<{ rows: UnbilledAudit[] }>({ queryKey: ['commissions-audit-unbilled', year], queryFn: ({ signal }) => api.get(`/api/commissions/audit/unbilled-savings${year ? `?year=${year}` : ''}`, signal), enabled: tab === 'audit' });
  const agentFor = firmWide ? (employee && employee !== 'house' ? employee : '') : (me?.id ?? '');
  const auditAgent = useQuery<{ rows: ByAgentAudit[] }>({ queryKey: ['commissions-audit-agent', agentFor], queryFn: ({ signal }) => api.get(`/api/commissions/audit/by-agent?user_id=${agentFor}`, signal), enabled: tab === 'audit' && !!agentFor });

  const markPaid = useMutation({
    mutationFn: (ids: string[]) => api.post<{ marked: number; skipped: number }>('/api/commissions/mark-paid', { invoice_ids: ids }),
    onSuccess: (r) => {
      toast({ variant: 'success', message: `${r.marked} invoice${r.marked === 1 ? '' : 's'} marked Commission Paid${r.skipped ? `, ${r.skipped} skipped (not paid, or already closed)` : ''}.` });
      setSelected({});
      queryClient.invalidateQueries({ queryKey: ['commissions-report'] });
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  const data = report.data;
  const groups = useMemo(() => [...(data?.invoices ?? []), ...(data?.non_billable ?? [])], [data]);
  const yearOptions = useMemo(() => {
    const ys = new Set<number>();
    for (const g of groups) if (g.year) ys.add(g.year);
    return [...ys].sort((a, b) => b - a);
  }, [groups]);
  const propertyOptions = useMemo(() => (properties.data ?? []).filter(p => !clientId || p.client_id === clientId), [properties.data, clientId]);
  const closable = groups.filter(g => g.invoice_id && g.commission_status === 'Paid');
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const exportHref = (fmt: 'csv' | 'pdf') => `/api/commissions/report.${fmt}${params ? `?${params}` : ''}`;

  const selectStyle = { minWidth: 160 } as const;

  return (
    <>
      <PageHeader
        title="Commissions"
        subtitle={data ? (data.scope === 'all' ? 'Every invoice, every person. Filter down to one.' : 'Your lines only, beside each invoice total.') : 'Loading…'}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="md" onClick={() => window.location.assign(exportHref('csv'))}>Export CSV</Button>
            <Button variant="secondary" size="md" onClick={() => window.location.assign(exportHref('pdf'))}>Export PDF</Button>
            <Button variant="ghost" size="md" onClick={() => navigate('/commissions/ledger')}>Ledger</Button>
          </div>
        }
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end', marginBottom: 'var(--sp-4)' }}>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Year</span>
          <Select aria-label="Year" value={year} onChange={(e) => setYear(e.target.value)} style={selectStyle}
            options={[{ value: '', label: 'All years' }, ...yearOptions.map(y => ({ value: String(y), label: String(y) }))]} />
        </label>
        {firmWide && (
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Employee</span>
            <Select aria-label="Employee" value={employee} onChange={(e) => setEmployee(e.target.value)} style={selectStyle}
              options={[{ value: '', label: 'Everyone' }, ...users.options, { value: 'house', label: 'ATA (house)' }]} />
          </label>
        )}
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Client</span>
          <Select aria-label="Client" value={clientId} onChange={(e) => { setClientId(e.target.value); setPropertyId(''); }} style={selectStyle}
            options={[{ value: '', label: 'All clients' }, ...(clients.data ?? []).map(c => ({ value: c.id, label: c.name ?? '—' }))]} />
        </label>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Property</span>
          <Select aria-label="Property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} style={selectStyle}
            options={[{ value: '', label: 'All properties' }, ...propertyOptions.map(p => ({ value: p.id, label: p.name ?? '—' }))]} />
        </label>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Status</span>
          <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}
            options={[{ value: '', label: 'All statuses' }, ...(['Mailed', 'Paid', 'Void', 'NonBillable', 'Commission Paid'] as Status[]).map(s => ({ value: s, label: s }))]} />
        </label>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Invoiced from</span>
          <Input type="date" aria-label="Invoiced from" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Invoiced to</span>
          <Input type="date" aria-label="Invoiced to" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      <Tabs
        items={[
          { value: 'report', label: 'Report', count: groups.length },
          { value: 'audit', label: 'Audit' },
          ...(firmWide ? [{ value: 'close', label: 'Year-end close', count: closable.length }] : []),
        ]}
        activeValue={tab}
        onChange={setTab}
      />

      {report.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading commissions…</p>}
      {report.error && <p style={{ color: 'var(--color-status-red)' }}>Could not load the commissions report.</p>}

      {tab === 'report' && data && (
        <div style={{ display: 'grid', gap: 'var(--sp-5)', marginTop: 'var(--sp-4)' }}>
          <section>
            <h3 style={sectionTitle}>Per person</h3>
            {data.per_employee.length === 0 ? <Empty>No commission lines match these filters.</Empty> : (
              <table style={tableStyle} data-testid="per-employee">
                <thead><tr>{['Person', 'Lines', 'Mailed', 'Paid', 'Commission Paid', 'NonBillable', 'Total invoiced', 'Total arrived'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.per_employee.map(e => (
                    <tr key={e.employee}>
                      <td style={tdStyle}><strong>{e.name}</strong></td>
                      <td style={tdStyle}>{e.lines}</td>
                      {(['Mailed', 'Paid', 'Commission Paid'] as Status[]).map(s => (
                        <td key={s} style={tdStyle}>{e.by_status[s] ? money(e.by_status[s].invoiced) : '—'}</td>
                      ))}
                      {/* A non-billable line carries no money by definition; the count of property-years is the fact. */}
                      <td style={tdStyle}>{e.by_status.NonBillable ? `${e.by_status.NonBillable.lines} ${e.by_status.NonBillable.lines === 1 ? 'line' : 'lines'}` : '—'}</td>
                      <td style={tdStyle}>{money(e.total_invoiced)}</td>
                      <td style={tdStyle}>{money(e.total_arrived)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 style={sectionTitle}>Invoices</h3>
            {groups.length === 0 ? <Empty>No invoices match these filters.</Empty> : (
              <table style={tableStyle} data-testid="invoices">
                <thead><tr>{['', 'Invoice', 'Property', 'Client', 'Year', 'Status', 'Invoice date', 'Last paid', 'Amount'].map((h, i) => <th key={i} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {groups.map(g => {
                    const key = g.invoice_id ?? `${g.property_id}|${g.year}`;
                    const isOpen = open[key] ?? true;
                    return (
                      <Fragment key={key}>
                        <tr style={{ background: 'var(--color-bg)' }}>
                          <td style={tdStyle}><button type="button" aria-label={isOpen ? 'Collapse' : 'Expand'} onClick={() => setOpen(o => ({ ...o, [key]: !isOpen }))} style={disclosure}>{isOpen ? '−' : '+'}</button></td>
                          <td style={tdStyle}>{g.invoice_number ? <button type="button" onClick={() => navigate(`/invoices/${g.invoice_id}`)} style={linkStyle}>{g.invoice_number}</button> : <span style={{ color: 'var(--color-ink-3)' }}>no invoice</span>}</td>
                          <td style={tdStyle}>{g.property_name ?? '—'}</td>
                          <td style={tdStyle}>{g.client_name ?? '—'}</td>
                          <td style={tdStyle}>{g.year ?? '—'}</td>
                          <td style={tdStyle}><Badge variant={STATUS_VARIANT[g.commission_status]}>{g.commission_status}</Badge></td>
                          <td style={tdStyle}>{g.invoice_date ?? '—'}</td>
                          <td style={tdStyle}>{g.last_paid_date ?? '—'}</td>
                          <td style={tdStyle}><strong>{money(g.invoice_amount)}</strong></td>
                        </tr>
                        {isOpen && g.allocations.map(a => (
                          <tr key={a.id} data-testid="allocation">
                            <td style={tdStyle} />
                            <td style={tdStyle} colSpan={2}><span style={{ color: 'var(--color-ink-3)' }}>{roleLabel(a)}</span> · {a.assignee_name}</td>
                            <td style={tdStyle} colSpan={3}>{a.percent}%</td>
                            <td style={tdStyle} colSpan={2}>Invoiced {money(a.amount_invoiced)}</td>
                            <td style={tdStyle}><ArrivedCell a={a} status={g.commission_status} /></td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}

      {tab === 'audit' && (
        <div style={{ display: 'grid', gap: 'var(--sp-5)', marginTop: 'var(--sp-4)' }}>
          <section>
            <h3 style={sectionTitle}>Splits that need attention</h3>
            <p style={hint}>Properties whose split does not total 100, or leaves a bucket the firm default expects empty.</p>
            {(auditSplits.data?.rows ?? []).length === 0 ? <Empty>Every property split totals 100.</Empty> : (
              <table style={tableStyle} data-testid="audit-splits">
                <thead><tr>{['Property', 'Client', 'Total', 'Missing'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>{auditSplits.data!.rows.map(r => (
                  <tr key={r.deal_id}><td style={tdStyle}>{r.property_name ?? '—'}</td><td style={tdStyle}>{r.client_name ?? '—'}</td>
                    <td style={{ ...tdStyle, color: r.total === 100 ? 'var(--color-ink)' : 'var(--color-status-red)' }}>{r.has_template ? `${r.total}%` : 'no split'}</td>
                    <td style={tdStyle}>{r.missing_labels.join(', ') || '—'}</td></tr>
                ))}</tbody>
              </table>
            )}
          </section>
          <section>
            <h3 style={sectionTitle}>Savings with no invoice</h3>
            <p style={hint}>Properties with a settled reduction in the year and no invoice raised for it.</p>
            {(auditUnbilled.data?.rows ?? []).length === 0 ? <Empty>Every settled saving has an invoice.</Empty> : (
              <table style={tableStyle} data-testid="audit-unbilled">
                <thead><tr>{['Property', 'Client', 'Year', 'Reduction', 'Appeals'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>{auditUnbilled.data!.rows.map(r => (
                  <tr key={`${r.property_id}|${r.tax_year}`}><td style={tdStyle}>{r.property_name}</td><td style={tdStyle}>{r.client_name ?? '—'}</td><td style={tdStyle}>{r.tax_year}</td><td style={tdStyle}>{Number(r.reduction).toLocaleString()}</td><td style={tdStyle}>{r.appeals}</td></tr>
                ))}</tbody>
              </table>
            )}
          </section>
          <section>
            <h3 style={sectionTitle}>{firmWide ? 'One person’s properties' : 'My properties'}</h3>
            <p style={hint}>{firmWide ? 'Pick an employee in the filter above to list their properties and splits.' : 'Every property you hold a split on, so you can verify your own setup.'}</p>
            {!agentFor ? <Empty>Choose an employee to see their property list.</Empty> : (auditAgent.data?.rows ?? []).length === 0 ? <Empty>No properties carry a split for this person.</Empty> : (
              <table style={tableStyle} data-testid="audit-by-agent">
                <thead><tr>{['Property', 'Client', 'Their share', 'Split total', 'Lines'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>{auditAgent.data!.rows.map(r => (
                  <tr key={r.deal_id}><td style={tdStyle}>{r.property_name ?? '—'}</td><td style={tdStyle}>{r.client_name ?? '—'}</td><td style={tdStyle}><strong>{r.my_percent}%</strong></td>
                    <td style={{ ...tdStyle, color: r.total === 100 ? 'var(--color-ink)' : 'var(--color-status-red)' }}>{r.total}%</td>
                    <td style={tdStyle}>{r.lines.map(l => `${l.role_label} ${l.assignee_name} ${l.percent}%`).join(' · ')}</td></tr>
                ))}</tbody>
              </table>
            )}
          </section>
        </div>
      )}

      {tab === 'close' && firmWide && (
        <section style={{ marginTop: 'var(--sp-4)' }}>
          <h3 style={sectionTitle}>Year-end close</h3>
          <p style={hint}>Once commission has been paid out on these Paid invoices, mark them Commission Paid so next year&apos;s report excludes them. Mailed invoices roll forward and become payable when they are paid.</p>
          {closable.length === 0 ? <Empty>No paid invoices are waiting to be closed.</Empty> : (
            <>
              <table style={tableStyle} data-testid="close-list">
                <thead><tr>{['', 'Invoice', 'Property', 'Year', 'Last paid', 'Amount'].map((h, i) => <th key={i} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>{closable.map(g => (
                  <tr key={g.invoice_id}>
                    <td style={tdStyle}><input type="checkbox" aria-label={`Select ${g.invoice_number}`} checked={!!selected[g.invoice_id!]} onChange={(e) => setSelected(s => ({ ...s, [g.invoice_id!]: e.target.checked }))} /></td>
                    <td style={tdStyle}>{g.invoice_number}</td><td style={tdStyle}>{g.property_name ?? '—'}</td><td style={tdStyle}>{g.year ?? '—'}</td><td style={tdStyle}>{g.last_paid_date ?? '—'}</td><td style={tdStyle}>{money(g.invoice_amount)}</td>
                  </tr>
                ))}</tbody>
              </table>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                <Button variant="secondary" size="sm" onClick={() => setSelected(Object.fromEntries(closable.map(g => [g.invoice_id!, true])))}>Select all</Button>
                <Button variant="primary" size="sm" disabled={selectedIds.length === 0} loading={markPaid.isPending} onClick={() => markPaid.mutate(selectedIds)}>
                  {selectedIds.length ? `Mark ${selectedIds.length} as Commission Paid` : 'Mark as Commission Paid'}
                </Button>
              </div>
            </>
          )}
        </section>
      )}
    </>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-3)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)' }}>{children}</div>;
}

const sectionTitle = { margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase' as const, letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' };
const hint = { margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' as const, fontSize: 'var(--fs-sm)', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)' };
const thStyle = { textAlign: 'left' as const, padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink-3)', fontWeight: 'var(--fw-medium)' as const };
const tdStyle = { padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink)', verticalAlign: 'top' as const };
const disclosure = { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' };
const linkStyle = { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' as const, padding: 0 };
